/**
 * Language resolution — pure logic, isolated so it can be tested without
 * initialising i18next or touching storage.
 */

export const SUPPORTED_LANGUAGES = ['fr', 'en'] as const;
export type Language = (typeof SUPPORTED_LANGUAGES)[number];

export const DEFAULT_LANGUAGE: Language = 'fr';

export function isSupported(
  value: string | null | undefined,
): value is Language {
  return SUPPORTED_LANGUAGES.includes(value as Language);
}

/**
 * Resolves which language to use.
 *
 * Priority: the reader's explicit choice, then the browser or OS languages **in
 * their order of preference**, then the default.
 *
 * Warning: The whole `navigator.languages` list is walked, not just
 * `navigator.language`: someone whose first language is unsupported
 * (`['de', 'en-US', 'fr']`) must get their second choice — English — rather
 * than the French default. Reading only the first entry ignored an explicit
 * preference.
 *
 * Regional variants are reduced to their base language (`en-GB` → `en`) and
 * the case is normalised: some browsers answer `fr-FR`, others `fr-fr`.
 */
export function resolveLanguage(
  stored: string | null | undefined,
  preferred: readonly string[],
): Language {
  if (isSupported(stored)) return stored;

  for (const tag of preferred) {
    const base = tag.split('-')[0]?.toLowerCase();
    if (isSupported(base)) return base;
  }

  return DEFAULT_LANGUAGE;
}

/**
 * The preferred languages as the platform exposes them.
 *
 * `navigator.languages` is the ordered list — the browser's "preferred
 * languages for displaying pages" setting — while `navigator.language` is only
 * its first entry, kept as a fallback for environments that expose no list.
 *
 * @returns The preferred languages, most wanted first.
 */
export function preferredLanguages(): readonly string[] {
  const nav = globalThis.navigator as Navigator | undefined;
  if (!nav) return [];
  if (nav.languages?.length) return nav.languages;
  return nav.language ? [nav.language] : [];
}
